// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CautionsList from "../../shared/CautionsList";
import { FigureRow, Figures } from "../../shared/FigureRow";
import SummaryCard, { NothingToSummarise } from "../../shared/SummaryCard";
import StereoLadderChart from "./StereoLadderChart";
import TrialReviewSection, {
  StereoLadderModuleType,
} from "../TrialReviewSection/TrialReviewSection";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

// Utils
import { num } from "../../shared/formatters";

type SummaryType = NonNullable<
  ExamSectionReportType["postProcessing"]["summary"]
>;
type StereoLadderSummaryType = SummaryType["StereoLadder"];

type SummarySectionType = {
  summary: SummaryType | null | undefined;
  stereoLadderModule: StereoLadderModuleType;
};

const MODULE_LABELS: Record<string, string> = {
  StereoLadder: "Summary",
};

const StereoLadderCard = ({
  data,
  stereoLadderModule,
}: {
  data: StereoLadderSummaryType;
  stereoLadderModule: StereoLadderModuleType;
}) => {
  const { t } = useTranslation();
  const presentationScale = stereoLadderModule?.presentationScale;
  return (
    <SummaryCard title={t(MODULE_LABELS.StereoLadder)} cautions={null}>
      {!data ? (
        <NothingToSummarise />
      ) : (
        <>
          <StereoLadderChart
            thresholdArcseconds={data.thresholdArcseconds}
            levels={stereoLadderModule?.levels}
            ladderArcsec={stereoLadderModule?.ladderArcsec}
          />
          <Figures>
            <FigureRow
              label={t("Threshold")}
              value={
                <Typography fontSize="12px">
                  {num(data.thresholdArcseconds, 0, "″")} {t("arcsec")}
                </Typography>
              }
            />
            <FigureRow
              label={t("At or Below")}
              value={
                <Typography fontSize="12px">
                  {data.atOrBelow ? t("Yes") : t("No")}
                </Typography>
              }
            />
            <FigureRow
              label={t("Interpretation")}
              value={
                <Typography fontSize="12px">
                  {data.interpretation ? t(data.interpretation) : t("N/A")}
                </Typography>
              }
            />
            {presentationScale !== undefined && presentationScale !== 1 && (
              <FigureRow
                label={t("Presentation Scale")}
                value={
                  <Typography fontSize="12px">
                    {t(
                      "{{scale}}× (trial): the slide and its disparities were drawn larger than the reported values",
                      { scale: presentationScale }
                    )}
                  </Typography>
                }
              />
            )}
          </Figures>
          <Box sx={{ mt: "8px", py: "8px", borderTop: "1px solid #5B6D7A40" }}>
            <Typography
              fontSize="12px"
              sx={{
                color: "#5B6D7A",
                textTransform: "uppercase",
                mb: "4px",
              }}
            >
              {t("Cautions")}
            </Typography>
            <CautionsList cautions={data.cautions} />
          </Box>
        </>
      )}
    </SummaryCard>
  );
};

// The technician view: the few headline numbers read off the module, built straight from
// postProcessing.summary, plus the trial-by-trial review which needs the verbose modules block.
const SummarySection = ({
  summary,
  stereoLadderModule,
}: SummarySectionType) => {
  const { t } = useTranslation();

  if (!summary) return null;

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap="16px" sx={{ mb: "8px" }}>
        <Box display="flex" flexDirection="column" gap="4px" sx={{ flex: 1 }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase" }}
          >
            {t("Section Cautions")}
          </Typography>
          <CautionsList cautions={summary.cautions} />
        </Box>
        <Box display="flex" flexDirection="column" gap="4px" sx={{ flex: 1 }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase" }}
          >
            {t("Incomplete Modules")}
          </Typography>
          {!summary.incompleteModules ||
          summary.incompleteModules.length === 0 ? (
            <Typography fontSize="12px">{t("N/A")}</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap="4px">
              {summary.incompleteModules.map((key) => (
                <Typography key={key} sx={{ fontSize: "12px" }}>
                  {t(MODULE_LABELS[key] ?? key)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <StereoLadderCard
          data={summary.StereoLadder}
          stereoLadderModule={stereoLadderModule}
        />
        <TrialReviewSection stereoLadder={stereoLadderModule} />
      </Box>
    </Box>
  );
};

export default SummarySection;
